import {
  UISubscriptionStatus,
  SubscriptionCancelReason,
  SubscriptionCancelReasonOption,
} from "common/types";

import { IClient } from "client/accounts/types";
import { StatusColor } from "client/common/components/content/Status";

import { OnboardingClient, OnboardingSubscriptionsStep } from "../types";

const exhaustiveCheck = (step: never) => {
  console.warn(`No inferred step for ${step}`);
  return undefined;
};

type OnboardingInferStepOpts = {
  onboarding: OnboardingClient;
  client: IClient;
};

const tryControlledSubstances = ({ onboarding }: OnboardingInferStepOpts) => {
  return onboarding.has_missing_controlled_substances_required_fields
    ? OnboardingSubscriptionsStep.ControlledSubstances
    : OnboardingSubscriptionsStep.Complete;
};

const tryPatientMissingFields = (opts: OnboardingInferStepOpts) => {
  const { onboarding } = opts;
  return onboarding.subscriptions
    .filter((sub) => sub.visible_status !== UISubscriptionStatus.Canceled)
    .some((sub) => sub.patient.missing_checkout_fields.length)
    ? OnboardingSubscriptionsStep.PatientMissingFields
    : tryControlledSubstances(opts);
};

const tryProfileMissingFields = (opts: OnboardingInferStepOpts) => {
  const { client } = opts;
  return client.primary_client_user.missing_checkout_fields.length
    ? OnboardingSubscriptionsStep.ProfileMissingFields
    : tryPatientMissingFields(opts);
};

const tryBilling = (opts: OnboardingInferStepOpts) => {
  const { client, onboarding } = opts;
  const hasPaymentMethod = !!(
    client.primary_payment_method || onboarding.subscriptions.every((sub) => sub.payment_method_id)
  );
  return !hasPaymentMethod || !client.primary_address
    ? OnboardingSubscriptionsStep.Billing
    : tryProfileMissingFields(opts);
};

export const tryList = (opts: OnboardingInferStepOpts) => {
  const { onboarding } = opts;
  if (
    onboarding.subscriptions.some(
      (subscription) => subscription.visible_status === UISubscriptionStatus.Imported,
    )
  ) {
    return OnboardingSubscriptionsStep.List;
  }
  return onboarding.subscriptions.some(
    (subscription) => subscription.visible_status !== UISubscriptionStatus.Canceled,
  )
    ? tryBilling(opts)
    : OnboardingSubscriptionsStep.Complete;
};

export const inferNextStep = ({
  prevStep,
  ...inferParams
}: {
  prevStep: OnboardingSubscriptionsStep;
  onboarding: OnboardingClient;
  client: IClient;
}): OnboardingSubscriptionsStep | undefined => {
  switch (prevStep) {
    case OnboardingSubscriptionsStep.List:
    case OnboardingSubscriptionsStep.Complete:
      return undefined;
    case OnboardingSubscriptionsStep.QuantityFrequency:
      return OnboardingSubscriptionsStep.NextOrderDate;
    case OnboardingSubscriptionsStep.Cancel:
    case OnboardingSubscriptionsStep.NextOrderDate:
      return tryList(inferParams);
    case OnboardingSubscriptionsStep.Billing:
      return tryProfileMissingFields(inferParams);
    case OnboardingSubscriptionsStep.ProfileMissingFields:
    case OnboardingSubscriptionsStep.PatientMissingFields:
      return tryPatientMissingFields(inferParams);
    case OnboardingSubscriptionsStep.ControlledSubstances:
      return tryControlledSubstances(inferParams);
    default:
      return exhaustiveCheck(prevStep);
  }
};

export const inferPreviousStep = (step: OnboardingSubscriptionsStep) => {
  switch (step) {
    case OnboardingSubscriptionsStep.List:
    case OnboardingSubscriptionsStep.Billing:
    case OnboardingSubscriptionsStep.ProfileMissingFields:
    case OnboardingSubscriptionsStep.PatientMissingFields:
    case OnboardingSubscriptionsStep.ControlledSubstances:
    case OnboardingSubscriptionsStep.Complete:
      return undefined;
    case OnboardingSubscriptionsStep.QuantityFrequency:
    case OnboardingSubscriptionsStep.NextOrderDate:
    case OnboardingSubscriptionsStep.Cancel:
      return OnboardingSubscriptionsStep.List;
    default:
      return exhaustiveCheck(step);
  }
};

type StepDefinition = {
  stepIndex: number;
};

type StepToDefinition = {
  [K in OnboardingSubscriptionsStep]: StepDefinition;
};

export const calcTotalSteps = (stepToDefinition: StepToDefinition) =>
  Math.max(
    ...Object.values(OnboardingSubscriptionsStep).map(
      (stepName) => stepToDefinition[stepName].stepIndex,
    ),
  ) + 1;

const exhaustiveColorCheck = (status: never): StatusColor => {
  console.warn(`invalid subscription status provided: ${status}`);
  return "greyscale";
};

export const getColorForStatus = (status: UISubscriptionStatus): StatusColor => {
  switch (status) {
    case UISubscriptionStatus.Active:
      return "success";
    case UISubscriptionStatus.Canceled:
    case UISubscriptionStatus.FailedPayment:
      return "danger";
    case UISubscriptionStatus.Imported:
      return "greyscale";
    case UISubscriptionStatus.Paused:
      return "warning";
    default:
      return exhaustiveColorCheck(status);
  }
};

export const onboardingSubscriptionCancelReasonOptions = (): SubscriptionCancelReasonOption[] => {
  const reasonToLabel: Partial<Record<SubscriptionCancelReason, string>> = {
    [SubscriptionCancelReason.ChangeInTreatment]: "My pet has switched to a different product",
    [SubscriptionCancelReason.Expensive]: "Item is too expensive on the new store",
    [SubscriptionCancelReason.ExcessProduct]: "I have enough supply on hand",
    [SubscriptionCancelReason.SubscriptionNotWanted]: "I don't want an Autoship subscription",
    [SubscriptionCancelReason.NoLongerClientOfClinic]: "I am no longer a client of this clinic",
    [SubscriptionCancelReason.PatientInactive]: "I no longer have this pet",
    [SubscriptionCancelReason.Other]: "Other (fill in reason below)",
  };
  return Object.entries(reasonToLabel).map(([value, label]) => ({
    value: value as SubscriptionCancelReason,
    label: label as string,
  }));
};
